import {getPixelsFromRem, Show, VStack} from 'platform/foundation';

import {useId, useMemo, useRef} from 'react';
import ReactSelect, {
  createFilter,
  type InputActionMeta,
  OnChangeValue,
  type SelectInstance,
} from 'react-select';

import {defaultTo, isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {MENU_MAX_HEIGHT} from '../../constants/menuMaxHeight';
import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {FormControlProps} from '../../types/FormControlProps';
import {OptionTypeBase} from '../../types/OptionTypeBase';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {LabelActions} from '../Label/LabelActions';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {ChoiceProvider} from './components/ChoiceProvider';
import {ChoiceCommonProps} from './types';
import {selectComponents} from './utils/components';
import {formatOptionFieldLabel} from './utils/formatOptionFieldLabel';
import {getChoiceCSS} from './utils/getChoiceCSS';
import {getFlattenedOptions} from './utils/getFlattenedOptions';
import {reactSelectStyles} from './utils/reactSelectStyles';
import {selectOption} from './utils/selectOption';

export interface ChoiceProps<ValueType>
  extends FormControlProps<ValueType | null>, TestIdProps, ChoiceCommonProps<ValueType, ValueType> {
  'aria-label'?: string;
  horizontalMenuPlacement?: 'left' | 'right';
  labelActions?: LabelActions;
  /**
   * @about Called with the text typed into the control, and react-select's reason for
   * the change: `input-change` when the user types, `input-blur` or `menu-close` when
   * the control clears it. For a caller that searches its options itself — pair it
   * with `filterOption={() => true}` so the options it returns are not filtered again.
   */
  onInputChange?: (value: string, meta: InputActionMeta) => void;
}

export function Choice<ValueType>(props: ChoiceProps<ValueType>) {
  const selectRef = useRef<SelectInstance<OptionTypeBase<ValueType | Nullish>, false> | null>(null);
  const t = useTranslationContext();
  const theme = useComponentsTheme();
  const id = useId();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const onSelectMount = (
    select: SelectInstance<OptionTypeBase<ValueType | Nullish>, false> | null
  ) => {
    if (select) {
      selectOption(select);
    }

    selectRef.current = select;
  };

  const onMenuOpen = () => {
    if (selectRef.current) {
      selectOption(selectRef.current);
    }
  };

  const handleChange = (options: OnChangeValue<OptionTypeBase<ValueType | Nullish>, false>) => {
    if (!options) {
      props.onChange?.(null);
      return;
    }

    props.onChange?.(options.value ?? null);
  };

  const getOptionsByValue = (value: ValueType | Nullish) => {
    if (isNil(value)) {
      return null;
    }

    const flatOptions = getFlattenedOptions(props.options);

    return flatOptions?.find((o) => o.value === value) ?? null;
  };

  // This is a workaround for an issue in react-select, when using custom components
  // https://github.com/JedWatson/react-select/issues/5489
  const components = useMemo(() => selectComponents<ValueType, false>(), []);

  const defaultFilterOption = useMemo(
    () =>
      createFilter({
        stringify: (option) => `${option.label}`,
        ignoreCase: true,
      }),
    []
  );

  return (
    <div
      onKeyDown={(event) => {
        // We want to stop propagation when the choice is located in a modal, so that closing the option list doesn't close the modal itself.
        if (event.key === 'Escape') {
          event.stopPropagation();
        }
      }}
    >
      <VStack>
        <Label
          id={id}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          isRecommended={props.isRecommended}
          actions={props.labelActions}
          data-testid={suffixTestId('label', props)}
        >
          {props.label}
        </Label>
        <ChoiceProvider
          options={{
            'data-testid': props['data-testid'],
            'data-name': props.name,
            hasOptionCheckbox: props.hasOptionCheckbox,
            isInvalid,
            isRequired: props.isRequired,
          }}
        >
          <ReactSelect
            aria-label={props['aria-label']}
            inputId={id}
            tabIndex={props.tabIndex}
            autoFocus={props.autoFocus}
            formatOptionLabel={props.formatOptionLabel ?? formatOptionFieldLabel}
            defaultValue={getOptionsByValue(props.defaultValue)}
            ref={onSelectMount}
            menuPortalTarget={props.menuInPortal ? document.body : undefined}
            value={getOptionsByValue(props.value)}
            name={props.name}
            isOptionDisabled={(option) => option.isDisabled ?? false}
            placeholder={defaultTo(t('general.labels.select'), props.placeholder)}
            onInputChange={(value, meta) => {
              /**
               * workaround
               * https://github.com/JedWatson/react-select/issues/3832
               */
              if (props.menuIsOpen) {
                selectRef?.current?.inputRef?.focus();
              }
              props.onInputChange?.(value, meta);
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={handleChange}
            isDisabled={props.isDisabled}
            options={props.options}
            isClearable={!props.isNotClearable}
            isSearchable={props.isSearchable ?? true}
            components={components}
            hideSelectedOptions={false}
            tabSelectsValue={false}
            menuPlacement={props.menuPlacement ?? 'auto'}
            maxMenuHeight={getPixelsFromRem(props.maxMenuHeight ?? MENU_MAX_HEIGHT)}
            styles={reactSelectStyles<ValueType, false>(theme, props.horizontalMenuPlacement)}
            onMenuOpen={onMenuOpen}
            noOptionsMessage={props.noOptionsMessage}
            closeMenuOnScroll={props.closeMenuOnScroll}
            closeMenuOnSelect={props.closeMenuOnSelect}
            isLoading={props.isLoading}
            filterOption={props.filterOption ?? defaultFilterOption}
            // Styling ReactSelect is problematic so we need to use the css prop
            className={getChoiceCSS(isInvalid, props.isDisabled, props.isRecommended)}
          />
        </ChoiceProvider>
        <Show when={props.errorMessage ?? props.helperText}>
          <HelperText
            errorMessage={props.errorMessage}
            helperText={props.helperText}
            data-testid={suffixTestId('helper', props)}
          />
        </Show>
      </VStack>
    </div>
  );
}
